/**
 * Merge overlapping [start, end] ranges (inclusive indices).
 */
export function mergeIndexRanges(ranges: ReadonlyArray<readonly [number, number]>): [number, number][] {
  if (!ranges.length) {
    return [];
  }
  const sorted = ranges
    .map(([a, b]) => (a <= b ? [a, b] : [b, a]) as [number, number])
    .sort((x, y) => x[0] - y[0]);
  const out: [number, number][] = [[sorted[0][0], sorted[0][1]]];
  for (let i = 1; i < sorted.length; i++) {
    const [s, e] = sorted[i];
    const last = out[out.length - 1];
    if (s <= last[1] + 1) {
      last[1] = Math.max(last[1], e);
    } else {
      out.push([s, e]);
    }
  }
  return out;
}

/**
 * Drop short Fuse highlight runs so fuzzy substrings (e.g. "an", "es" inside "assessment")
 * do not paint unrelated words. Single-word queries need stronger pruning than multi-word.
 */
export function pruneTrivialHighlightSpans(
  indices: ReadonlyArray<readonly [number, number]>,
  query: string
): [number, number][] {
  const merged = mergeIndexRanges(indices);
  const q = query.trim();
  if (!q.length) {
    return merged;
  }
  const wordCount = q.split(/\s+/).filter(Boolean).length;
  let minSpan = 1;

  if (wordCount === 1) {
    if (q.length >= 8) {
      minSpan = Math.max(minSpan, Math.ceil(q.length * 0.55));
    } else if (q.length >= 6) {
      minSpan = Math.max(minSpan, 3);
    }
  } else {
    if (q.length >= 10 && wordCount >= 2) {
      minSpan = Math.max(minSpan, 2);
    }
    if (q.length >= 18 && wordCount >= 2) {
      minSpan = Math.max(minSpan, 3);
    }
  }

  if (minSpan <= 1) {
    return merged;
  }
  return merged.filter(([s, e]) => e - s + 1 >= minSpan);
}

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Wrap matched ranges in `<mark class="timeline-search-hit">` after escaping each segment.
 * Indices are inclusive [start, end] on `plainText`.
 */
export function applyHighlightMarks(plainText: string, indices: ReadonlyArray<readonly [number, number]>): string {
  if (!plainText) {
    return '';
  }
  if (!indices.length) {
    return escapeHtml(plainText);
  }
  const merged = mergeIndexRanges(indices);
  const parts: string[] = [];
  let cursor = 0;
  for (const [start, end] of merged) {
    if (start > end || start >= plainText.length) {
      continue;
    }
    const e = Math.min(end, plainText.length - 1);
    if (cursor < start) {
      parts.push(escapeHtml(plainText.slice(cursor, start)));
    }
    parts.push(
      `<mark class="timeline-search-hit">${escapeHtml(plainText.slice(start, e + 1))}</mark>`
    );
    cursor = e + 1;
  }
  if (cursor < plainText.length) {
    parts.push(escapeHtml(plainText.slice(cursor)));
  }
  return parts.join('');
}
